import PasswordComplexity from '../components/PasswordComplexity';
import Indicator from '../../riskindicators/Indicator';
import MalwareKitsDrawer from '../../catalogs/features/malwares/components/MalwareKitsDrawer';
import List from '@/components/content/list/List';
import InformationReliability from '@/components/content/informationreliability/InformationReliability';
import ThreatActorsDrawer from '../../catalogs/features/threatactors/components/ThreatActorDrawer';
import VulnerabilitiesDrawer from '../../catalogs/features/vulnerabilities/components/VulnerabilitiesDrawer';
import SourcesDrawer from '../../catalogs/features/sources/components/SourcesDrawer';

import { createHelper } from '../../issue/utils/helper';
import { ModuleLayouts, type Module } from '../../issue/types/modulesv2';
import { getEntries, getValues } from '@/utils';
import { formatIsoDate } from '@/utils/date';

import { CREDENTIALS_EXPOSURE_TYPE_MAP, SignalEntity } from '../types';
import { Group } from '@mantine/core';
import RiskLevelIndicator from '@/client/features/signals/components/RiskLevelIndicator';

const helper = createHelper<SignalEntity>();

export const credentialLeft = (): Array<Module<SignalEntity>> => [
  {
    title: 'Credentials Leak Information',
    submodules: [
      helper.accessor((data) => data.credential.domain, {
        title: 'Associated domains',
      }),
      helper.accessor((data) => data.credential.username, {
        title: 'Username',
      }),
      helper.accessor((data) => data.attributes.email, {
        title: 'Exposed Email',
      }),
      helper.accessor((data) => data.credential.leaked_password, {
        title: 'Leaked Password',
      }),
      helper.accessor((data) => data.credentials, {
        title: 'Leaked Password Complexity',
        element: ({ getValue }) => {
          return <PasswordComplexity {...getValue()} />;
        },
      }),
      helper.accessor((data) => data.credential.hash, {
        title: 'Password SHA256',
      }),
      helper.accessor(
        (data) => ({
          hostname: data.credential.hostname,
          level: data.credential.hostnameRiskLevel,
        }),
        {
          title: 'Related hostname',
          element: ({ getValue }) => {
            const { hostname, level } = getValue();

            return (
              <Group gap={6} wrap="nowrap">
                <span>{hostname}</span>
                {level && <RiskLevelIndicator level={level} />}
              </Group>
            );
          },
        }
      ),
      helper.accessor((data) => data.attributes.ip, {
        title: 'Exposed IP',
      }),
      helper.accessor(
        (data) => ({ url: data.credential.url, level: data.credential.pathRiskLevel }),
        {
          title: 'URL of Login',
          element: ({ getValue }) => {
            const { url, level } = getValue();

            return (
              <Group gap={6} wrap="nowrap">
                <span>{url}</span>
                {level && <RiskLevelIndicator level={level} />}
              </Group>
            );
          },
        }
      ),
      helper.accessor((data) => data.timestamp, {
        title: 'Lookup Timestamp',
      }),
    ],
  },
  {
    title: 'Compromised Host Information',
    submodules: [
      helper.accessor((data) => data.credential.domain, {
        title: 'Local Hostname',
      }),
      helper.accessor((data) => data.hardwareId, {
        title: 'Hardware ID',
      }),
      helper.accessor((data) => data.country, {
        title: 'Country',
      }),
      helper.accessor((data) => data.loggedUsername, {
        title: 'Logged Username',
      }),
      helper.accessor((data) => data.localLanguage, {
        title: 'Local Language',
      }),
      helper.accessor((data) => data.location, {
        title: 'Location',
      }),
      helper.accessor((data) => data.ipAddress, {
        title: 'IP Address',
      }),
      helper.accessor((data) => data.operatingSystem, {
        title: 'Operating System',
      }),
      helper.accessor((data) => data.localTimestamp, {
        title: 'Local Timestamp',
      }),
      helper.accessor((data) => data.localTimezone, {
        title: 'Local Timezone',
      }),
    ],
  },
];

export const credentialRight = (): Array<Module<SignalEntity>> => [
  {
    submodules: [
      helper.accessor((data) => data.credential.type, {
        title: 'Risk Indicators',
        element: ({ getValue }) => {
          return <Indicator hasIndicator={true} text={CREDENTIALS_EXPOSURE_TYPE_MAP[getValue()]} />;
        },
        scroll: false,
      }),
    ],
  },
  {
    title: 'Signal Information',
    submodules: [
      helper.accessor((data) => data.id, {
        title: 'ID',
      }),
      helper.accessor((data) => 'Credential Leak', {
        title: 'Category',
      }),
      helper.accessor((data) => formatIsoDate(data.ingested_at), {
        title: 'Ingested At',
      }),
      helper.accessor((data) => formatIsoDate(data.published_at), {
        title: 'Published At',
      }),
      helper.accessor((data) => formatIsoDate(data.updated_at), {
        title: 'Updated At',
      }),
    ],
  },
  {
    title: 'Source Information',
    submodules: [
      helper.accessor(
        (data) => {
          return data.sourceInformation;
        },
        {
          title: 'Source',
          element: ({ getValue }) => {
            const { id, name } = getValue();

            return <SourcesDrawer id={id} name={name || `${id}`} />;
          },
        }
      ),
      helper.accessor(
        (data) => ({ type: data.sourceInformation.type, type2: data.sourceInformation.subtype }),
        {
          title: 'Source Type',
          element: ({ getValue }) => {
            const { type, type2 } = getValue();

            return `${type} - ${type2}`;
          },
        }
      ),
      helper.accessor((data) => data.sourceInformation?.reliability, {
        title: 'Source Reliability Score',
        element: ({ getValue }) => <InformationReliability value={getValue()} />,
      }),
      helper.accessor((data) => data.credential.url, {
        title: 'URL',
      }),
      helper.accessor((data) => formatIsoDate(data.sourceInformation.updated_at), {
        title: 'Source DTG',
      }),
    ],
  },
  {
    title: 'Related Entities',
    layout: ModuleLayouts.GRID,
    submodules: [
      helper.accessor((data) => getEntries(data.catalog.threatactor), {
        title: 'Threat Actors',
        element: ({ getValue }) => (
          <List
            items={getValue()}
            renderItem={([id, name]) => {
              return <ThreatActorsDrawer id={+id} name={name} />;
            }}
          />
        ),
      }),
      helper.accessor(
        (data) => {
          return getEntries(data.catalog.vulnerability);
        },
        {
          title: 'Vulnerabilities',
          element: ({ getValue }) => (
            <List
              items={getValue()}
              renderItem={([id, name]) => {
                return <VulnerabilitiesDrawer id={+id} name={name} />;
              }}
            />
          ),
        }
      ),
      helper.accessor((data) => getEntries(data.catalog.malwarekit), {
        title: 'Malware Kits',
        element: ({ getValue }) => (
          <List
            items={getValue()}
            renderItem={([id, name]) => {
              return <MalwareKitsDrawer id={+id} name={name} />;
            }}
          />
        ),
      }),
      helper.accessor((data) => getValues(data.catalog.technologies), {
        title: 'Technologies',
      }),
    ],
  },
];
